import { Box, Stack, Typography } from "@mui/material";
import { getWasteSubmissionFormName } from "encamp-shared/src/submissions/wasteSubmissionForms";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionStepType } from "generated-graphql/graphql";
import { useWasteStepper } from "hooks/useWasteStepper";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { formatDateLikeForDisplay } from "util/dates";

import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { StepContinueFooter } from "./StepContinueFooter";

/**
 * Understand step (document forms): what this submission is, why it is
 * required, and its scope. Rendered entirely from the shell query — the step
 * fetches no data of its own. First step of the flow, so no Back.
 */
export function UnderstandStep({ submissionId }: { submissionId: string; facilityId: string }) {
  const { submission } = useWasteStepper();

  // Named forms (8700-13) show their EPA form name; one-off combos fall back
  // to the submission type.
  const formName =
    getWasteSubmissionFormName(submission?.submissionType, submission?.submissionMethod) ??
    (submission?.submissionType ? prettyPrintEnumValue(submission.submissionType) : "");
  const notes = submission?.notes ?? "";
  const facility = submission?.facilitySubmissions?.[0]?.facility;
  const facilityDisplay = [facility?.name, facility?.state].filter(Boolean).join(", ");

  return (
    <Stack spacing={3}>
      <StepHeader
        title="Understand"
        description="What this submission is, why it is required, and what it covers."
      />
      <DisplayOnlyField label="Form" text={formName} />
      <Stack spacing={1}>
        <StepSubHeader title="Why it's required" />
        {/* The requirement description entered at creation. Requirement
            citations will render here once they ship and this becomes the
            fallback. */}
        {notes ? (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {notes}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No requirement description has been added.
          </Typography>
        )}
      </Stack>
      <Stack spacing={1}>
        <StepSubHeader title="Scope" />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <DisplayOnlyField label="Facility" text={facilityDisplay} />
          </Box>
          <Box sx={{ width: 220 }}>
            <DisplayOnlyField
              label="Due date"
              text={submission?.dueDate ? formatDateLikeForDisplay(submission.dueDate) : ""}
            />
          </Box>
          <Box sx={{ width: 220 }}>
            <DisplayOnlyField
              label="Submission method"
              text={
                submission?.submissionMethod
                  ? prettyPrintEnumValue(submission.submissionMethod)
                  : ""
              }
            />
          </Box>
        </Box>
      </Stack>
      <StepContinueFooter
        submissionId={submissionId}
        step={SubmissionStepType.Understand}
        label="Continue"
      />
    </Stack>
  );
}
